import { useGlobalAction } from "@gadgetinc/react";
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
  TrashIcon,
} from "@heroicons/react/16/solid";
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useContext, useEffect, useState } from "react";
import { api } from "../../../../api";
import { codeStatusEnum } from "../../../../enums/customCouponCodes/codeStatus.enum";
import { OverflowFrame } from "../../../../externalComponents";
import LoadingSpinner from "../../../../externalComponents/LoadingSpinner";
import LoadingText from "../../../../externalComponents/LoadingText";
import { PageHeading } from "../../../../externalComponents/PageHeading";
import { SettingsSidebar } from "../../../../externalComponents/SettingsSidebar";
import { Badge } from "../../../../externalComponents/tailwind/badge";
import { Button } from "../../../../externalComponents/tailwind/button";
import EmptyState from "../../../../externalComponents/tailwind/empty-state";
import { Field } from "../../../../externalComponents/tailwind/fieldset";
import { Select } from "../../../../externalComponents/tailwind/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../externalComponents/tailwind/table";
import { Text } from "../../../../externalComponents/tailwind/text";
import { CustomerContext } from "../../../../providers";
import DeleteAllUnusedCouponsDialog from "./DeleteAllUnusedCouponsDialog";
import DeleteCouponDialog from "./DeleteCouponDialog";
import ImportCouponDialog from "./ImportCouponDialog";

const LOW_STOCK_THRESHOLD = 10000;
const PAGE_SIZE = 10;

export default function CouponCodesPage() {
  const { customer } = useContext(CustomerContext);
  const [refreshSummary, setRefreshSummary] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [
    showDeleteAllUnusedCouponsDialog,
    setShowDeleteAllUnusedCouponsDialog,
  ] = useState(false);
  const [showDeleteAllCouponsDialog, setShowDeleteAllCouponsDialog] =
    useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [couponCodesSummaryByCategory, setCouponCodesSummaryByCategory] =
    useState({
      fetching: false,
      data: { total: null, available: null, used: null, expired: null },
    });

  const [
    { data: searchCouponsData, fetching: searchCouponsFetching },
    searchCoupons,
  ] = useGlobalAction(api.settings.searchCouponCodes);

  const [{ fetching: exportCouponsFetching }, exportCoupons] = useGlobalAction(
    api.settings.exportCouponCodes,
  );

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.getCustomCouponCodeCategories();
        setCategories(
          response.customCouponCodes
            ?.map(({ category }) => category)
            .filter(Boolean),
        );
      } catch (error) {
        setCategories([]);
        console.error("Error checking billing status:", error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (!categoryFilter || !refreshSummary) {
      return;
    }

    setRefreshSummary(false);
    const fetchCoupons = async () => {
      try {
        setCouponCodesSummaryByCategory((state) => ({
          ...state,
          fetching: true,
        }));
        const response = await api.getCustomCouponCodeSummaryByCategory({
          category: categoryFilter,
        });
        setCouponCodesSummaryByCategory({
          fetching: false,
          data: response,
        });
      } catch (error) {
        console.error("Error fetching coupon codes summary:", error);
        setCouponCodesSummaryByCategory({
          fetching: false,
          data: { total: null, available: null, used: null, expired: null },
        });
      }
    };

    fetchCoupons();
  }, [categoryFilter, refreshSummary]);

  const coupons = searchCouponsData?.coupons || [];

  const isLowStock =
    couponCodesSummaryByCategory.data.available <= LOW_STOCK_THRESHOLD &&
    couponCodesSummaryByCategory.data.total > 0;

  const hasNextPage = searchCouponsData?.hasNextPage || false;
  const hasPreviousPage = searchCouponsData?.hasPreviousPage || false;

  const handleExport = useCallback(async () => {
    const { data: _exportCouponsData } = await exportCoupons({
      category: categoryFilter,
      customerId: customer.id,
    });
    if (_exportCouponsData) {
      const blob = new Blob([_exportCouponsData], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Format du nom de fichier: YYYYMMdd-HHmm_CID_companyId_category_coupons_codes.csv
      const now = new Date();
      const YYYY = now.getFullYear();
      const MM = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      const HH = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      const timestamp = `${YYYY}${MM}${dd}-${HH}${mm}`;
      const filename = `${timestamp}_CID_${customer.id}_${categoryFilter}_coupons_codes.csv`;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  }, [categoryFilter, customer.id]);

  const handleNextPage = useCallback(() => {
    if (searchCouponsData?.endCursor) {
      const paginationParams = {
        first: PAGE_SIZE,
        after: searchCouponsData.endCursor,
      };
      setPageNumber((p) => p + 1);
      searchCoupons({
        category: categoryFilter || undefined,
        ...paginationParams,
      });
    }
  }, [searchCouponsData?.endCursor, categoryFilter]);

  const handlePreviousPage = useCallback(() => {
    if (searchCouponsData?.startCursor) {
      const paginationParams = {
        last: PAGE_SIZE,
        before: searchCouponsData.startCursor,
      };
      setPageNumber((p) => Math.max(1, p - 1));
      searchCoupons({
        category: categoryFilter || undefined,
        ...paginationParams,
      });
    }
  }, [searchCouponsData?.startCursor, categoryFilter]);

  const handleCategoryChange = useCallback(
    (_category) => {
      if (!_category) {
        return;
      }
      const paginationParams = {
        first: PAGE_SIZE,
      };
      setCategoryFilter(_category);
      setRefreshSummary(true);
      setPageNumber(1);
      searchCoupons({
        category: _category || undefined,
        ...paginationParams,
      });
    },
    [categories],
  );

  return (
    <>
      <SettingsSidebar>
        <PageHeading title="Coupon codes" />
        <OverflowFrame>
          <div className="mx-3 md:mx-0">
            {isLowStock && (
              <div className="my-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3">
                <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600" />
                <div>
                  <Text className="font-semibold text-yellow-800">
                    Low stock alert
                  </Text>
                  <Text className="text-yellow-700">
                    Only {couponCodesSummaryByCategory.data.available} coupon
                    {couponCodesSummaryByCategory.data.available !== 1
                      ? "s"
                      : ""}{" "}
                    remaining
                    {categoryFilter ? ` for category "${categoryFilter}"` : ""}.
                    Consider importing more codes.
                  </Text>
                </div>
              </div>
            )}
            {(searchCouponsData || searchCouponsFetching) && (
              <div className="my-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <Text className="text-sm text-gray-500">Total Coupons</Text>
                  {couponCodesSummaryByCategory.fetching ? (
                    <LoadingText />
                  ) : (
                    <Text className="text-2xl font-bold">
                      {couponCodesSummaryByCategory.data.total}
                    </Text>
                  )}
                </div>
                <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <Text className="text-sm text-gray-500">Available</Text>
                  {couponCodesSummaryByCategory.fetching ? (
                    <LoadingText />
                  ) : (
                    <Text className="text-2xl font-bold">
                      {couponCodesSummaryByCategory.data.available}
                    </Text>
                  )}
                </div>
                <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <Text className="text-sm text-gray-500">Used</Text>
                  {couponCodesSummaryByCategory.fetching ? (
                    <LoadingText />
                  ) : (
                    <Text className="text-2xl font-bold">
                      {couponCodesSummaryByCategory.data.used}
                    </Text>
                  )}
                </div>
                <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <Text className="text-sm text-gray-500">Expired</Text>
                  {couponCodesSummaryByCategory.fetching ? (
                    <LoadingText />
                  ) : (
                    <Text className="text-2xl font-bold">
                      {couponCodesSummaryByCategory.data.expired}
                    </Text>
                  )}
                </div>
              </div>
            )}
            <div className="my-4 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex gap-2 items-center">
                <Field className="inline-flex gap-3">
                  <Select
                    disabled={searchCouponsFetching}
                    value={categoryFilter}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="min-w-[200px]"
                  >
                    <option value="">Select a category</option>
                    {categories?.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </Select>
                  {categoryFilter && (
                    <Button
                      disabled={searchCouponsFetching}
                      onClick={() => {
                        handleCategoryChange(categoryFilter);
                        setRefreshSummary(true);
                      }}
                      color="primaryButton"
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                      Refresh
                    </Button>
                  )}
                </Field>
              </div>
              <div>
                <Button
                  disabled={
                    searchCouponsFetching ||
                    !categoryFilter ||
                    exportCouponsFetching
                  }
                  className="mr-2"
                  color="secondaryButton"
                  onClick={() => setShowDeleteAllUnusedCouponsDialog(true)}
                >
                  <TrashIcon className="w-4 h-4" />
                  Delete unused coupons
                </Button>
                <Button
                  disabled={
                    searchCouponsFetching ||
                    !categoryFilter ||
                    exportCouponsFetching
                  }
                  className="mr-2"
                  color="secondaryButton"
                  onClick={() => setShowDeleteAllCouponsDialog(true)}
                >
                  <TrashIcon className="w-4 h-4" />
                  Delete all coupons
                </Button>
                <Button
                  disabled={searchCouponsFetching || exportCouponsFetching}
                  className="mr-2"
                  color="primaryButton"
                  onClick={() => setShowImportDialog(true)}
                >
                  <ArrowUpTrayIcon className="w-4 h-4" />
                  Import coupons
                </Button>

                <Button
                  disabled={
                    searchCouponsFetching ||
                    !categoryFilter ||
                    exportCouponsFetching
                  }
                  color="primaryButton"
                  onClick={() => handleExport()}
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  Export category
                </Button>
              </div>
            </div>

            {searchCouponsFetching || exportCouponsFetching ? (
              <LoadingSpinner />
            ) : coupons && coupons.length > 0 ? (
              <>
                <Table className="mb-4">
                  <TableHead>
                    <TableRow>
                      <TableHeader>Code</TableHeader>
                      <TableHeader>Category</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Assigned to</TableHeader>
                      <TableHeader>Used at</TableHeader>
                      <TableHeader>Expired at</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {coupons.map((coupon) => (
                      <TableRow key={coupon.id}>
                        <TableCell className="font-mono">
                          {coupon.code}
                        </TableCell>
                        <TableCell>
                          <Badge color="zinc">{coupon.category || "-"}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            color={
                              coupon.status == codeStatusEnum.used
                                ? "zinc"
                                : coupon.status == codeStatusEnum.expired
                                  ? "amber"
                                  : "emerald"
                            }
                          >
                            {coupon.status == codeStatusEnum.used
                              ? "Used"
                              : coupon.status == codeStatusEnum.expired
                                ? "Expired"
                                : "Available"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {coupon.contact
                            ? `${coupon.contact.firstName || ""} ${coupon.contact.lastName || ""}`.trim() ||
                              "Contact"
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {coupon.usedAt
                            ? new Date(coupon.usedAt).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {coupon.expiredAt
                            ? new Date(coupon.expiredAt).toLocaleDateString()
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="flex justify-start my-4">
                  <Button
                    color="secondaryButton"
                    onClick={handlePreviousPage}
                    disabled={!hasPreviousPage}
                  >
                    <ArrowLeftIcon />
                    Previous
                  </Button>
                  <Badge className="my-1 mx-2 min-w-10 flex items-center justify-center">
                    {pageNumber}
                  </Badge>
                  <Button
                    color="secondaryButton"
                    onClick={handleNextPage}
                    disabled={!hasNextPage}
                  >
                    Next
                    <ArrowRightIcon />
                  </Button>
                </div>
              </>
            ) : (
              <>
                {categoryFilter && (
                  <EmptyState
                    heading="You have no coupon codes for this category"
                    content="Import a CSV file to add coupon codes for your customers."
                    action={() => setShowImportDialog(true)}
                    icon={<PlusIcon />}
                    actionText="Import coupon codes"
                  />
                )}
              </>
            )}
          </div>
        </OverflowFrame>
      </SettingsSidebar>

      <ImportCouponDialog
        open={showImportDialog}
        categories={categories}
        onClose={() => setShowImportDialog(false)}
        onSuccess={(categoryUsed) => {
          const categoryIndex = categories.indexOf(categoryUsed);
          if (categoryIndex < 0) {
            categories.push(categoryUsed);
          }
          setShowImportDialog(false);
        }}
      />

      <DeleteAllUnusedCouponsDialog
        open={showDeleteAllUnusedCouponsDialog}
        onClose={() => setShowDeleteAllUnusedCouponsDialog(false)}
        customerId={customer?.id}
        categoryFilter={categoryFilter}
      />

      <DeleteCouponDialog
        open={showDeleteAllCouponsDialog}
        onClose={() => setShowDeleteAllCouponsDialog(false)}
        category={categoryFilter}
        onSuccess={() => {
          setShowDeleteAllCouponsDialog(false);
          handleCategoryChange(categoryFilter);
          setRefreshSummary(true);
        }}
      />
    </>
  );
}
